import { forwardRef, useState } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn, INPUT_TRIGGER_CLS } from "@/lib/utils";

function toDate(v?: string | null): Date | undefined {
  if (!v) return undefined;
  const [y, m, d] = v.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}

function toYmd(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export const DatePicker = forwardRef<HTMLButtonElement, {
  value: string | null | undefined;
  onChange: (value: string) => void;
  id?: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  disableFuture?: boolean;
  defaultYear?: number;
}>(function DatePicker({
  value,
  onChange,
  id,
  disabled,
  placeholder = "Pick a date",
  className,
  disableFuture,
  defaultYear,
}, ref) {
  const [open, setOpen] = useState(false);
  const selected = toDate(value);
  const today = new Date();
  const thisYear = today.getFullYear();
  const defaultMonth = selected ?? (defaultYear != null ? new Date(defaultYear, 0) : undefined);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        ref={ref}
        id={id}
        disabled={disabled}
        className={cn(INPUT_TRIGGER_CLS, !selected && "text-muted-foreground", className)}
      >
        <span className="truncate text-left">
          {selected ? selected.toLocaleDateString() : placeholder}
        </span>
        <CalendarIcon className="h-4 w-4 shrink-0 opacity-50" />
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          captionLayout="dropdown"
          startMonth={new Date(1920, 0)}
          endMonth={new Date(disableFuture ? thisYear : thisYear + 10, 11)}
          defaultMonth={defaultMonth}
          selected={selected}
          disabled={disableFuture ? { after: today } : undefined}
          onSelect={(d) => {
            onChange(d ? toYmd(d) : "");
            setOpen(false);
          }}
          autoFocus
        />
      </PopoverContent>
    </Popover>
  );
});
